/**
 * JoinOrganizationForm - Form for joining an existing organization with invite code
 * Features code validation, formatting, and error handling
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useOrganization } from '../../context/OrganizationContext';
import { JoinOrganizationInput } from '../../types/organization';

interface JoinOrganizationFormProps {
  onSuccess: (organizationName: string) => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

export const JoinOrganizationForm: React.FC<JoinOrganizationFormProps> = ({
  onSuccess,
  loading,
  setLoading
}) => {
  const { joinOrganization, error, clearError } = useOrganization();
  const [inviteCode, setInviteCode] = useState<string>('');
  const [formattedCode, setFormattedCode] = useState<string>('');
  const [formError, setFormError] = useState<string>('');

  // Format invite code as XXX-XXX
  const formatInviteCode = useCallback((code: string): string => {
    // Remove all non-alphanumeric characters and convert to uppercase
    const cleaned = code.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    // Limit to 6 characters
    const limited = cleaned.substring(0, 6);
    
    // Add hyphen after 3rd character if we have more than 3 characters
    if (limited.length > 3) {
      return `${limited.substring(0, 3)}-${limited.substring(3)}`;
    }
    
    return limited;
  }, []);

  // Validate invite code format
  const validateInviteCode = useCallback((code: string): string => {
    if (!code) {
      return 'Invite code is required';
    }

    // Remove formatting for validation
    const cleanCode = code.replace('-', '');
    
    if (cleanCode.length !== 6) {
      return 'Invite code must be 6 characters';
    }

    if (!/^[A-Z0-9]{6}$/.test(cleanCode)) {
      return 'Invite code must contain only letters and numbers';
    }

    return '';
  }, []);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    setFormError('');
  }, [inviteCode, error, clearError]);

  const handleCodeChange = (value: string) => {
    const formatted = formatInviteCode(value);
    const clean = value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
    
    setFormattedCode(formatted);
    setInviteCode(clean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const validationError = validateInviteCode(inviteCode);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setLoading(true);
    setFormError('');

    try {
      const response = await joinOrganization({
        inviteCode: inviteCode
      });

      console.log('Successfully joined organization:', response);
      onSuccess(response.organization.displayName);
    } catch (err: any) {
      console.error('Failed to join organization:', err);
      
      // Provide user-friendly error messages
      let errorMessage = err.message || 'Failed to join organization';
      
      if (err.code === 'not-found' || errorMessage.includes('not found')) {
        errorMessage = 'Invalid invite code. Please check the code and try again.';
      } else if (err.code === 'failed-precondition' || errorMessage.includes('expired')) {
        errorMessage = 'This invite code has expired. Please request a new one.';
      } else if (errorMessage.includes('no uses remaining')) {
        errorMessage = 'This invite code has already been used. Please request a new one.';
      } else if (errorMessage.includes('already a member')) {
        errorMessage = 'You are already a member of this organization.';
      }
      
      setFormError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const displayError = formError || error;
  const codeValidationError = validateInviteCode(inviteCode);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Invite Code Field */}
      <div>
        <label htmlFor="inviteCode" className="block text-sm font-medium text-gray-700 mb-2">
          Invite Code
        </label>
        <div className="relative">
          <input
            id="inviteCode"
            type="text"
            value={formattedCode}
            onChange={(e) => handleCodeChange(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-center font-mono text-lg tracking-wider ${
              codeValidationError && inviteCode ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
            }`}
            placeholder="ABC-123"
            disabled={loading}
            maxLength={7} // 6 characters + 1 hyphen
            style={{ letterSpacing: '0.2em' }}
            required
          />
        </div>
        
        {codeValidationError && inviteCode ? (
          <p className="text-sm text-red-600 mt-1">{codeValidationError}</p>
        ) : (
          <p className="text-sm text-gray-500 mt-1">
            Enter the 6-character invite code provided by your organization administrator.
          </p>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">How to get an invite code:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600">
              <li>Ask your organization administrator to create one</li>
              <li>Check your email for an invitation</li>
              <li>Codes are typically 6 characters (letters and numbers)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {displayError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700">{displayError}</p>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading || !inviteCode.trim() || !!codeValidationError}
        className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Joining Organization...
          </div>
        ) : (
          'Join Organization'
        )}
      </button>
    </form>
  );
};